<script setup lang="ts">
import moment from "moment";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import DatePicker from "../../components/DatePicker.vue";
import Dropdown from "../../components/Dropdown.vue";
import FormField from "../../components/FormField.vue";
import Tab from "../../components/Tab.vue";
import TabGroup from "../../components/TabGroup.vue";
import TextArea from "../../components/TextArea.vue";
import Textbox from "../../components/Textbox.vue";
import type { DropdownOption } from "../../redistribution/models/DropdownOption.ts";
import type { PurchaseOrder } from "../../redistribution/models/PurchaseOrder.js";
import type { SearchFilters } from "../../services/purchaseOrderService.ts";
import {
  fetchCollections,
  fetchRequestIdByPoNumber,
  fetchVendors,
  fetchWarehouses,
  getCachedCollections,
  searchPurchaseOrders,
  searchPurchaseOrdersByPONumbers,
  searchPurchaseOrdersByRequestId,
} from "../../services/purchaseOrderService.ts";
import { useAppStore } from "../../stores/app.ts";
import { useModalStore } from "../../stores/modalStore.ts";
import { searchBulkIntermediateOrdersAndNavigate } from "./bulkIntermediateOrderSearch.ts";
import { useDemoIdentifierSearch } from "./featureFlags.ts";
import {
  classifySearchInput,
  getLoadingMessage,
  getNoResultsMessage,
  getSearchTypeLabel,
  type SearchTypeClassification,
} from "./searchTypeClassifier.ts";
import {
  createDemoPrimaryPurchaseOrder,
  createDemoRelatedPurchaseOrders,
} from "./utils/demoPurchaseOrderGenerator.ts";

const props = withDefaults(
  defineProps<{
    showTabs?: boolean;
    // Enables modal-specific framing/behavior when this panel is hosted inside a modal.
    showModalChrome?: boolean;
  }>(),
  {
    showTabs: true,
    // Default page mode: no modal header and no auto-close on submit.
    showModalChrome: false,
  }
);

const poNumbersInput = ref("");
const cuttingDateIdInput = ref("");
const relatedPoNumbersInput = ref("");
const relatedPurchaseOrderNumbers = ref<string[]>([]);

void cuttingDateIdInput.value;

/* ── Bulk Order Lookup (Intermediate Orders) search state ────────────────── */
const bulkOrderNumber = ref("");

const SESSION_COOKIE_NAMES = {
  purchaseOrderNumber: "PurchaseOrderNumber",
  purchaseOrderRequestId: "PurchaseOrderRequestId",
  purchaseOrderNumbers: "PurchaseOrderNumbers",
} as const;

const HISTORY_COOKIE_NAMES = {
  purchaseOrderNumber: "PurchaseOrderNumberHistory",
  purchaseOrderRequestId: "PurchaseOrderRequestIdHistory",
  purchaseOrderNumbers: "PurchaseOrderNumbersHistory",
} as const;

const COOKIE_MAX_AGE_SESSION_SECONDS = 8 * 60 * 60;
const COOKIE_MAX_AGE_HISTORY_SECONDS = 30 * 24 * 60 * 60;
const MAX_HISTORY_ENTRIES = 10;

function setCookie(name: string, value: string, maxAgeSeconds: number): void {
  // eslint-disable-next-line unicorn/no-document-cookie
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; SameSite=Lax; Max-Age=${String(maxAgeSeconds)}`;
}

function getCookie(name: string): string | undefined {
  const prefixedName = `${name}=`;
  const value = document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(prefixedName));
  if (!value) {
    return undefined;
  }
  return decodeURIComponent(value.slice(prefixedName.length));
}

function parseHistoryCookie(name: string): string[] {
  const raw = getCookie(name);
  if (!raw) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === "string")
      : [];
  } catch {
    return [];
  }
}

function updateHistoryCookie(name: string, entry: string | undefined): void {
  const normalizedEntry = entry?.trim();
  if (!normalizedEntry) {
    return;
  }

  const existing = parseHistoryCookie(name);
  const next = [
    normalizedEntry,
    ...existing.filter(
      (value) => value.toUpperCase() !== normalizedEntry.toUpperCase()
    ),
  ].slice(0, MAX_HISTORY_ENTRIES);

  setCookie(name, JSON.stringify(next), COOKIE_MAX_AGE_HISTORY_SECONDS);
}

function updatePurchaseOrderSearchCookies(args: {
  purchaseOrderNumber?: string | undefined;
  purchaseOrderRequestId?: string | undefined;
  purchaseOrderNumbers: string[];
}): void {
  const currentPurchaseOrderNumbers = args.purchaseOrderNumbers
    .map((value) => value.trim())
    .filter(Boolean);
  const purchaseOrderNumbersValue = currentPurchaseOrderNumbers.join(" ");

  setCookie(
    SESSION_COOKIE_NAMES.purchaseOrderNumbers,
    purchaseOrderNumbersValue,
    COOKIE_MAX_AGE_SESSION_SECONDS
  );

  const purchaseOrderNumber = args.purchaseOrderNumber?.trim();
  if (purchaseOrderNumber) {
    setCookie(
      SESSION_COOKIE_NAMES.purchaseOrderNumber,
      purchaseOrderNumber,
      COOKIE_MAX_AGE_SESSION_SECONDS
    );
    updateHistoryCookie(
      HISTORY_COOKIE_NAMES.purchaseOrderNumber,
      purchaseOrderNumber
    );
  }

  const purchaseOrderRequestId = args.purchaseOrderRequestId?.trim();
  if (purchaseOrderRequestId) {
    setCookie(
      SESSION_COOKIE_NAMES.purchaseOrderRequestId,
      purchaseOrderRequestId,
      COOKIE_MAX_AGE_SESSION_SECONDS
    );
    updateHistoryCookie(
      HISTORY_COOKIE_NAMES.purchaseOrderRequestId,
      purchaseOrderRequestId
    );
  }

  updateHistoryCookie(
    HISTORY_COOKIE_NAMES.purchaseOrderNumbers,
    purchaseOrderNumbersValue
  );
}

function extractUniquePurchaseOrderNumbers(
  purchaseOrders: PurchaseOrder[]
): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const purchaseOrder of purchaseOrders) {
    const normalized = purchaseOrder.purchaseOrderNumber.trim();
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    unique.push(normalized);
  }
  return unique;
}

const appStore = useAppStore();
const modalStore = useModalStore();
const route = useRoute();
const router = useRouter();
const {
  warehouseIds,
  vendorId,
  color,
  collection,
  arrivalDateFrom,
  arrivalDateTo,
  rateOfSaleOptions,
  warehouseOptions,
  warehousesError,
  warehousesLoading,
  vendorOptions,
  vendorsError,
  vendorsLoading,
  collectionOptions,
  collectionsError,
  collectionsLoading,
  searching,
  activeSearchTab,
  searchPoNumbersInput,
} = storeToRefs(appStore);

watch(
  [() => props.showTabs, activeSearchTab],
  ([showTabs]) => {
    if (showTabs && activeSearchTab.value === "purchase-order-search") {
      activeSearchTab.value = "Vendor Search";
    }
  },
  { immediate: true }
);

const {
  resetPoSearchResultsContext,
  setPoSearchResultsContext,
  setSearchResults,
  beginTypedSearch,
  clearTypedSearchPresentation,
} = appStore;
const COLOR_UPPERCASE_DELAY_MS = 1500;

function closeSearchModalAfterSubmit(): void {
  // In modal mode, close the dialog immediately after submit actions.
  if (props.showModalChrome) {
    //showModalChrome means this panel is hosted inside a modal and renders with a modal header
    modalStore.close();
  }
}

const normalizeColor = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed.toUpperCase() : undefined;
};

const hasSelectedDate = (value: Date | null | undefined): value is Date => {
  return value instanceof Date && !Number.isNaN(value.getTime());
};

const disabledDateFrom = (date: Date): boolean => {
  if (!hasSelectedDate(arrivalDateTo.value)) return false;
  return moment(date).isAfter(arrivalDateTo.value, "day");
};

const disabledDateTo = (date: Date): boolean => {
  if (!hasSelectedDate(arrivalDateFrom.value)) return false;
  return moment(date).isBefore(arrivalDateFrom.value, "day");
};

const clampArrivalDateFrom = (date: Date): Date => {
  if (
    hasSelectedDate(arrivalDateTo.value) &&
    moment(date).isAfter(arrivalDateTo.value, "day")
  ) {
    return new Date(arrivalDateTo.value);
  }
  return date;
};

const clampShipDateTo = (date: Date): Date => {
  if (
    hasSelectedDate(arrivalDateFrom.value) &&
    moment(date).isBefore(arrivalDateFrom.value, "day")
  ) {
    return new Date(arrivalDateFrom.value);
  }
  return date;
};

const vendorLabel = computed(() => {
  return vendorId.value?.length === 4 ? vendorId.value : "";
});

interface WarehouseSummaryPart {
  text: string;
  bold: boolean;
}

function textPart(text: string): WarehouseSummaryPart {
  return { text, bold: false };
}

function boldPart(text: string): WarehouseSummaryPart {
  return { text, bold: true };
}

function joinPartGroups(
  groups: WarehouseSummaryPart[][]
): WarehouseSummaryPart[] {
  const normalizedGroups = groups.filter((group) => group.length > 0);
  if (normalizedGroups.length === 0) {
    return [];
  }

  const parts: WarehouseSummaryPart[] = [];
  for (const [index, group] of normalizedGroups.entries()) {
    parts.push(...group);
    if (index < normalizedGroups.length - 2) {
      parts.push(textPart(", "));
      continue;
    }
    if (index === normalizedGroups.length - 2) {
      parts.push(textPart(" and "));
    }
  }

  return parts;
}

function createWarehouseNameGroup(labels: string[]): WarehouseSummaryPart[] {
  const nameGroups = labels.map((label) => [boldPart(label)]);
  const names = joinPartGroups(nameGroups);
  if (names.length === 0) {
    return [];
  }

  return [
    ...names,
    textPart(labels.length === 1 ? " warehouse" : " warehouses"),
  ];
}

function createDistributionCenterGroup(
  division: string,
  selectedInDivision: DropdownOption[]
): WarehouseSummaryPart[] {
  const distributionCenterLabel = getGroupHeaderLabel(division);
  const warehouseLabels = selectedInDivision.map((option) => option.label);

  return [
    boldPart(distributionCenterLabel),
    textPart(" distribution center ("),
    ...createWarehouseNameGroup(warehouseLabels),
    textPart(")"),
  ];
}

const vendorWarehouseHelperParts = computed<WarehouseSummaryPart[]>(() => {
  if (vendorLabel.value.length !== 4 || warehouseIds.value.length === 0) {
    return [];
  }

  const selectedOptions = warehouseIds.value
    .map((warehouseId) =>
      warehouseOptions.value.find((option) => option.value === warehouseId)
    )
    .filter((option): option is DropdownOption => option !== undefined);

  if (selectedOptions.length === 0) {
    return [];
  }

  const selectedByDivision = new Map<string, DropdownOption[]>();
  for (const option of selectedOptions) {
    const division = option.metadata?.division as string | undefined;
    if (!division) {
      continue;
    }

    const existing = selectedByDivision.get(division) ?? [];
    existing.push(option);
    selectedByDivision.set(division, existing);
  }

  const segmentGroups: WarehouseSummaryPart[][] = [];
  const plainWarehouseLabels: string[] = [];

  for (const [division, selectedInDivision] of selectedByDivision.entries()) {
    const allInDivision = warehouseOptions.value.filter(
      (option) => (option.metadata?.division as string | undefined) === division
    );

    const isAllInDistributionCenterSelected =
      allInDivision.length > 0 &&
      selectedInDivision.length === allInDivision.length;

    if (isAllInDistributionCenterSelected) {
      segmentGroups.push(
        createDistributionCenterGroup(division, selectedInDivision)
      );
      continue;
    }

    plainWarehouseLabels.push(
      ...selectedInDivision.map((option) => option.label)
    );
  }

  if (plainWarehouseLabels.length > 0) {
    segmentGroups.push(createWarehouseNameGroup(plainWarehouseLabels));
  }

  const selectedWarehouseParts = joinPartGroups(segmentGroups);
  if (selectedWarehouseParts.length === 0) {
    return [];
  }

  return [
    textPart("Filter open POs for the "),
    boldPart(vendorLabel.value),
    textPart(" vendor shipping to "),
    ...selectedWarehouseParts,
    textPart(". Select 2 or more POs to redistribute."),
  ];
});

const canSearch = computed(() => {
  return (
    warehouseIds.value.length > 0 &&
    vendorId.value !== undefined &&
    hasSelectedDate(arrivalDateFrom.value) &&
    hasSelectedDate(arrivalDateTo.value)
  );
});

interface SelectedWarehouseFilter {
  division: string;
  whsNumber: string;
}

function getSelectedWarehouseFilters(): SelectedWarehouseFilter[] {
  const selectedFilters: SelectedWarehouseFilter[] = [];

  for (const warehouseId of warehouseIds.value) {
    const [selectedDivision, selectedWarehouseNumber] = warehouseId.split("_");
    if (!selectedDivision || !selectedWarehouseNumber) {
      continue;
    }
    selectedFilters.push({
      division: selectedDivision,
      whsNumber: selectedWarehouseNumber,
    });
  }

  return selectedFilters;
}

const searchDisabledReason = computed(() => {
  if (searching.value) {
    return "Search is in progress.";
  }

  const missingFields: string[] = [];
  if (vendorId.value === undefined) {
    missingFields.push("Vendor");
  }
  if (warehouseIds.value.length === 0) {
    missingFields.push("Warehouse");
  }
  if (!hasSelectedDate(arrivalDateFrom.value)) {
    missingFields.push("Arrival Date From");
  }
  if (!hasSelectedDate(arrivalDateTo.value)) {
    missingFields.push("Arrival Date To");
  }

  return missingFields.length > 0
    ? `Missing required fields: ${missingFields.join(", ")}.`
    : undefined;
});

const MAX_PO_NUMBERS = 1000;

const normalizedPoNumbersInput = computed(() =>
  normalizePoNumbersInput(poNumbersInput.value)
);

const poNumbersCount = computed(() =>
  normalizedPoNumbersInput.value.length === 0
    ? 0
    : normalizedPoNumbersInput.value.split(" ").filter(Boolean).length
);

const normalizedRelatedPoNumbersInput = computed(() =>
  normalizePoNumbersInput(relatedPoNumbersInput.value)
);

const relatedPoNumbersCount = computed(() =>
  normalizedRelatedPoNumbersInput.value.length === 0
    ? 0
    : normalizedRelatedPoNumbersInput.value.split(" ").filter(Boolean).length
);

function getPoNumbersSearchDisabledReason(
  normalizedInput: string,
  count: number
): string | undefined {
  if (searching.value) {
    return "Search is in progress.";
  }
  if (normalizedInput.length === 0) {
    return "Enter at least one PO number.";
  }
  return count > MAX_PO_NUMBERS
    ? `Too many PO numbers (${count.toLocaleString()}). The maximum is ${MAX_PO_NUMBERS.toLocaleString()}.`
    : undefined;
}

function canSearchNormalizedPoNumbers(
  normalizedInput: string,
  count: number
): boolean {
  return (
    normalizedInput.length > 0 && count <= MAX_PO_NUMBERS && !searching.value
  );
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return "Unknown error";
}

function buildSinglePoFallbackSearchInput(poNumber: string): string {
  const normalized = poNumber.toUpperCase().replaceAll("/", "");
  if (normalized.length <= 7 || !/^\d{7}[A-Z0-9]+$/.test(normalized)) {
    return poNumber;
  }

  const withSlash = `${normalized.slice(0, 7)}/${normalized.slice(7)}`;
  return [...new Set([poNumber, normalized, withSlash])].join(" ");
}

function normalizeTextboxPoNumbersInput(
  value: string | undefined
): string | undefined {
  return value == null ? undefined : normalizePoNumbersInput(value);
}

const canSearchByPONumbers = computed(() => {
  return canSearchNormalizedPoNumbers(
    normalizedPoNumbersInput.value,
    poNumbersCount.value
  );
});

const searchByPONumbersDisabledReason = computed<string | undefined>(() => {
  return getPoNumbersSearchDisabledReason(
    normalizedPoNumbersInput.value,
    poNumbersCount.value
  );
});

const canSearchRelatedPosFromCuttingDate = computed(() => {
  return canSearchNormalizedPoNumbers(
    normalizedRelatedPoNumbersInput.value,
    relatedPoNumbersCount.value
  );
});

const searchRelatedPosFromCuttingDateDisabledReason = computed<
  string | undefined
>(() => {
  return getPoNumbersSearchDisabledReason(
    normalizedRelatedPoNumbersInput.value,
    relatedPoNumbersCount.value
  );
});

function sanitizeForLog(value: string): string {
  return value
    .replaceAll(/[\r\n]+/g, " ")
    .replaceAll(/[\u0000-\u001F\u007F]+/g, "");
}

async function searchRelatedPurchaseOrders(
  normalizedSubmittedPoNumbers: string,
  useDemoRelatedResults = false
): Promise<void> {
  searching.value = true;
  searchPoNumbersInput.value = poNumbersInput.value;
  setSearchResults([]);
  resetPoSearchResultsContext();
  try {
    const submittedPoNumbers = normalizedSubmittedPoNumbers
      .split(" ")
      .filter(Boolean);
    let requestIdFallbackError: string | undefined;
    relatedPurchaseOrderNumbers.value = [];
    const submittedNormalizedPoNumbers = submittedPoNumbers.join(" ");
    console.info("[RediSku search] single-PO search start", {
      submittedPoNumbers,
      useDemoRelatedResults,
    });
    let results: PurchaseOrder[];
    if (useDemoRelatedResults) {
      const primaryPoNumberFromInput = submittedPoNumbers[0] ?? "DEMO-PO";
      results = [createDemoPrimaryPurchaseOrder(primaryPoNumberFromInput)];
    } else {
      results = await searchPurchaseOrdersByPONumbers(
        submittedNormalizedPoNumbers
      );
      console.info("[RediSku search] initial PO-number search complete", {
        submittedPoNumbers,
        resultCount: results.length,
        purchaseOrderNumbers: extractUniquePurchaseOrderNumbers(results),
      });

      if (submittedPoNumbers.length === 1 && results.length === 0) {
        const fallbackSearchInput = buildSinglePoFallbackSearchInput(
          submittedPoNumbers[0] ?? ""
        );
        if (fallbackSearchInput !== submittedNormalizedPoNumbers) {
          results = await searchPurchaseOrdersByPONumbers(fallbackSearchInput);
          console.info(
            "[RediSku search] alternate PO formatting search complete",
            {
              fallbackSearchInput,
              resultCount: results.length,
              purchaseOrderNumbers: extractUniquePurchaseOrderNumbers(results),
            }
          );
        }
      }

      if (submittedPoNumbers.length === 1 && results.length === 0) {
        try {
          const resolvedRequestId = await fetchRequestIdByPoNumber(
            submittedPoNumbers[0] ?? ""
          );

          if (resolvedRequestId !== undefined) {
            results = await searchPurchaseOrdersByRequestId(resolvedRequestId);
            console.info(
              "[RediSku search] request-id fallback search complete",
              {
                sourcePoNumber: submittedPoNumbers[0],
                resolvedRequestId,
                resultCount: results.length,
                purchaseOrderNumbers:
                  extractUniquePurchaseOrderNumbers(results),
              }
            );
          }
        } catch (error) {
          requestIdFallbackError = getErrorMessage(error);
          console.warn(
            "Single-PO request-id fallback failed",
            requestIdFallbackError
              ? sanitizeForLog(requestIdFallbackError)
              : requestIdFallbackError
          );
          // Keep PO-number behavior stable: a request-id fallback failure should not
          // turn an empty PO result into an error state.
        }
      }
    }
    setSearchResults(results);

    if (submittedPoNumbers.length !== 1) {
      updatePurchaseOrderSearchCookies({
        purchaseOrderNumbers: submittedPoNumbers,
      });
      setPoSearchResultsContext({
        mode: "flat",
        primaryPoNumber: undefined,
        relatedPurchaseOrders: [],
        helperMessage:
          "Related PO expansion is available only when searching for a single PO number.",
        helperMessageTone: "info",
      });
      return;
    }

    const primaryPurchaseOrder = results[0];
    const primaryPoNumber = primaryPurchaseOrder?.purchaseOrderNumber;
    if (!primaryPurchaseOrder || results.length !== 1) {
      setPoSearchResultsContext({
        mode: "flat",
        primaryPoNumber,
        relatedPurchaseOrders: [],
        helperMessage: requestIdFallbackError
          ? `PO lookup returned no results, and request-id fallback failed: ${requestIdFallbackError}`
          : undefined,
        helperMessageTone: requestIdFallbackError ? "error" : undefined,
      });
      return;
    }

    const requestId = primaryPurchaseOrder.purchaseOrderRequestId;
    const normalizedRequestId =
      typeof requestId === "number" ? String(requestId) : requestId?.trim();
    const hasRequestId =
      typeof requestId === "number" ||
      (typeof requestId === "string" && requestId.trim().length > 0);
    if (!useDemoRelatedResults && !hasRequestId) {
      setPoSearchResultsContext({
        mode: "flat",
        primaryPoNumber,
        relatedPurchaseOrders: [],
        helperMessage: undefined,
        helperMessageTone: undefined,
      });
      return;
    }

    let relatedPurchaseOrders: PurchaseOrder[];

    if (useDemoRelatedResults) {
      relatedPurchaseOrders =
        createDemoRelatedPurchaseOrders(primaryPurchaseOrder);
    } else {
      let relatedSearchResults: PurchaseOrder[];
      const requestIdForSearch =
        typeof requestId === "number" ? requestId : (requestId ?? "").trim();
      try {
        relatedSearchResults =
          await searchPurchaseOrdersByRequestId(requestIdForSearch);
        console.info("[RediSku search] related PO search complete", {
          primaryPoNumber: primaryPurchaseOrder.purchaseOrderNumber,
          purchaseOrderRequestId: normalizedRequestId,
          relatedResultCount: relatedSearchResults.length,
          purchaseOrderNumbers:
            extractUniquePurchaseOrderNumbers(relatedSearchResults),
        });
      } catch {
        updatePurchaseOrderSearchCookies({
          purchaseOrderNumber: primaryPoNumber,
          purchaseOrderRequestId: normalizedRequestId,
          purchaseOrderNumbers: [primaryPoNumber].filter(
            (value): value is string =>
              typeof value === "string" && value.length > 0
          ),
        });
        setPoSearchResultsContext({
          mode: "flat",
          primaryPoNumber,
          relatedPurchaseOrders: [],
          helperMessage:
            "Unable to load related purchase orders right now. Showing primary results only.",
          helperMessageTone: "error",
        });
        return;
      }

      relatedPurchaseOrderNumbers.value =
        extractUniquePurchaseOrderNumbers(relatedSearchResults);
      relatedPoNumbersInput.value = relatedPurchaseOrderNumbers.value.join(" ");
      relatedPurchaseOrders = relatedSearchResults.filter(
        (purchaseOrder) =>
          purchaseOrder.purchaseOrderNumber !==
          primaryPurchaseOrder.purchaseOrderNumber
      );
    }

    if (relatedPurchaseOrders.length === 0) {
      updatePurchaseOrderSearchCookies({
        purchaseOrderNumber: primaryPoNumber,
        purchaseOrderRequestId: normalizedRequestId,
        purchaseOrderNumbers:
          relatedPurchaseOrderNumbers.value.length > 0
            ? relatedPurchaseOrderNumbers.value
            : [primaryPoNumber].filter(
                (value): value is string =>
                  typeof value === "string" && value.length > 0
              ),
      });
      setPoSearchResultsContext({
        mode: "flat",
        primaryPoNumber,
        relatedPurchaseOrders: [],
        helperMessage: undefined,
        helperMessageTone: undefined,
      });
      return;
    }

    const relatedPurchaseOrder = relatedPurchaseOrders[0];
    const helperMessage = useDemoRelatedResults
      ? `Demo mode: showing ${relatedPurchaseOrders.length.toString()} randomized related purchase orders for ${primaryPurchaseOrder.purchaseOrderNumber}.`
      : relatedPurchaseOrders.length === 1 && relatedPurchaseOrder
        ? `Showing ${relatedPurchaseOrder.purchaseOrderNumber} as a related purchase order for ${primaryPurchaseOrder.purchaseOrderNumber}.`
        : `Showing ${relatedPurchaseOrders.length.toString()} related purchase orders for ${primaryPurchaseOrder.purchaseOrderNumber}.`;

    if (useDemoRelatedResults) {
      relatedPurchaseOrderNumbers.value = extractUniquePurchaseOrderNumbers([
        primaryPurchaseOrder,
        ...relatedPurchaseOrders,
      ]);
      relatedPoNumbersInput.value = relatedPurchaseOrderNumbers.value.join(" ");
    }

    updatePurchaseOrderSearchCookies({
      purchaseOrderNumber: primaryPoNumber,
      purchaseOrderRequestId: normalizedRequestId,
      purchaseOrderNumbers:
        relatedPurchaseOrderNumbers.value.length > 0
          ? relatedPurchaseOrderNumbers.value
          : [primaryPoNumber].filter(
              (value): value is string =>
                typeof value === "string" && value.length > 0
            ),
    });

    setPoSearchResultsContext({
      mode: "single-po-related",
      primaryPoNumber,
      relatedPurchaseOrders,
      helperMessage,
      helperMessageTone: "info",
    });
  } catch (error) {
    setPoSearchResultsContext({
      mode: "flat",
      primaryPoNumber: undefined,
      relatedPurchaseOrders: [],
      helperMessage: `Unable to load purchase orders right now. ${getErrorMessage(error)}`,
      helperMessageTone: "error",
    });
  } finally {
    searching.value = false;
  }
}

async function searchByPONumbers(): Promise<void> {
  closeSearchModalAfterSubmit();
  await runTypedInputSearch(poNumbersInput.value);
}

async function searchRelatedPosFromCuttingDate(): Promise<void> {
  closeSearchModalAfterSubmit();
  await runTypedInputSearch(relatedPoNumbersInput.value);
}

function createIdentifierDemoPurchaseOrders(
  identifier: string,
  count: number
): PurchaseOrder[] {
  const normalizedIdentifier = identifier
    .toUpperCase()
    .replaceAll(/[^A-Z0-9]/g, "")
    .slice(0, 8);

  return Array.from({ length: count }, (_, index) => {
    const suffix = String(index + 1).padStart(2, "0");
    return createDemoPrimaryPurchaseOrder(`${normalizedIdentifier}-${suffix}`);
  });
}

function searchByRequestIdDemo(requestId: string): void {
  const demoResults = createIdentifierDemoPurchaseOrders(requestId, 3);
  setSearchResults(demoResults);
  setPoSearchResultsContext({
    mode: "flat",
    primaryPoNumber: undefined,
    relatedPurchaseOrders: [],
    helperMessage: "Showing demo Request ID results.",
    helperMessageTone: "info",
  });
}

async function searchByRequestId(requestId: string): Promise<void> {
  const results = await searchPurchaseOrdersByRequestId(requestId);
  setSearchResults(results);
  setPoSearchResultsContext({
    mode: "flat",
    primaryPoNumber: undefined,
    relatedPurchaseOrders: [],
    helperMessage: undefined,
    helperMessageTone: undefined,
  });
}

function searchByCuttingDateDemo(cuttingDateToken: string): void {
  const demoResults = createIdentifierDemoPurchaseOrders(cuttingDateToken, 2);
  setSearchResults(demoResults);
  setPoSearchResultsContext({
    mode: "flat",
    primaryPoNumber: undefined,
    relatedPurchaseOrders: [],
    helperMessage: "Showing demo Cutting Date results.",
    helperMessageTone: "info",
  });
}

function searchByCuttingIdDemo(cuttingIdToken: string): void {
  const demoResults = createIdentifierDemoPurchaseOrders(cuttingIdToken, 2);
  setSearchResults(demoResults);
  setPoSearchResultsContext({
    mode: "flat",
    primaryPoNumber: undefined,
    relatedPurchaseOrders: [],
    helperMessage: "Showing demo Cutting ID results.",
    helperMessageTone: "info",
  });
}

async function runTypedInputSearch(rawInput: string): Promise<void> {
  const classification: SearchTypeClassification =
    classifySearchInput(rawInput);

  const searchTypeLabel = getSearchTypeLabel(classification.type);
  beginTypedSearch({
    searchTypeLabel,
    loadingMessage: getLoadingMessage(
      classification.type,
      classification.tokens.length
    ),
    noResultsMessage: getNoResultsMessage(classification.type),
  });

  if (classification.type === "invalid") {
    setSearchResults([]);
    resetPoSearchResultsContext();
    setPoSearchResultsContext({
      mode: "flat",
      primaryPoNumber: undefined,
      relatedPurchaseOrders: [],
      helperMessage: classification.reason,
      helperMessageTone: "error",
    });
    return;
  }

  if (classification.type === "po-numbers") {
    await searchRelatedPurchaseOrders(classification.normalizedInput, false);
    clearTypedSearchPresentation();
    return;
  }

  searching.value = true;
  setSearchResults([]);
  resetPoSearchResultsContext();

  try {
    if (classification.type === "request-id") {
      const requestIdToken =
        classification.tokens[0] ?? classification.normalizedInput;
      if (useDemoIdentifierSearch) {
        searchByRequestIdDemo(requestIdToken);
      } else {
        await searchByRequestId(requestIdToken);
      }
      return;
    }

    if (classification.type === "cutting-date") {
      if (useDemoIdentifierSearch) {
        searchByCuttingDateDemo(
          classification.tokens[0] ?? classification.normalizedInput
        );
      } else {
        setSearchResults([]);
        setPoSearchResultsContext({
          mode: "flat",
          primaryPoNumber: undefined,
          relatedPurchaseOrders: [],
          helperMessage: "Cutting Date search requires demo mode.",
          helperMessageTone: "error",
        });
      }
      return;
    }

    if (useDemoIdentifierSearch) {
      searchByCuttingIdDemo(
        classification.tokens[0] ?? classification.normalizedInput
      );
    } else {
      setSearchResults([]);
      setPoSearchResultsContext({
        mode: "flat",
        primaryPoNumber: undefined,
        relatedPurchaseOrders: [],
        helperMessage: "Cutting ID search requires demo mode.",
        helperMessageTone: "error",
      });
    }
  } catch (error) {
    setPoSearchResultsContext({
      mode: "flat",
      primaryPoNumber: undefined,
      relatedPurchaseOrders: [],
      helperMessage: `Unable to load purchase orders right now. ${getErrorMessage(error)}`,
      helperMessageTone: "error",
    });
  } finally {
    searching.value = false;
    clearTypedSearchPresentation();
  }
}

function normalizePoNumbersInput(value: string): string {
  return value
    .replaceAll(/[,;:\r\n\t]+/g, " ")
    .replaceAll(/ {2,}/g, " ")
    .trim();
}

function onPoNumbersPaste(event: ClipboardEvent): void {
  event.preventDefault();
  const textarea = event.target as HTMLTextAreaElement;
  const pasted = event.clipboardData?.getData("text") ?? "";
  const formatted = normalizePoNumbersInput(pasted);
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const current = poNumbersInput.value;
  poNumbersInput.value = normalizePoNumbersInput(
    [current.slice(0, start), formatted, current.slice(end)]
      .filter(Boolean)
      .join(" ")
  );
}

function onPoNumbersBlur(): void {
  poNumbersInput.value = normalizePoNumbersInput(poNumbersInput.value);
}

async function searchBulkOrderIntermediateOrders(): Promise<void> {
  const query = bulkOrderNumber.value.trim();
  if (!query) {
    return;
  }

  closeSearchModalAfterSubmit();
  await searchBulkIntermediateOrdersAndNavigate({
    query,
    appStore,
    router,
    route,
  });
}

async function search(): Promise<void> {
  closeSearchModalAfterSubmit();
  beginTypedSearch({
    searchTypeLabel: "Parameter Search",
    loadingMessage: "Searching by selected filters...",
    noResultsMessage:
      "No results found for the selected filters. Please check your input and try again.",
  });
  searching.value = true;
  setSearchResults([]);
  resetPoSearchResultsContext();
  try {
    if (route.name !== "search-select-pos") {
      await router.push({ name: "search-select-pos" });
    }

    const normalizedColor = normalizeColor(color.value);
    const selectedCollection = collection.value?.trim();
    const selectedArrivalDateFrom = hasSelectedDate(arrivalDateFrom.value)
      ? arrivalDateFrom.value
      : undefined;
    const selectedArrivalDateTo = hasSelectedDate(arrivalDateTo.value)
      ? arrivalDateTo.value
      : undefined;

    if (!selectedArrivalDateFrom || !selectedArrivalDateTo) {
      return;
    }

    const selectedWarehouseFilters = getSelectedWarehouseFilters();
    if (selectedWarehouseFilters.length === 0) {
      setSearchResults([]);
      return;
    }

    const warehouseSearchResults = await Promise.all(
      selectedWarehouseFilters.map((selectedWarehouseFilter) => {
        const filters: SearchFilters = {
          division: selectedWarehouseFilter.division,
          whsNumber: selectedWarehouseFilter.whsNumber,
          ...(vendorId.value ? { vendor: vendorId.value } : {}),
          ...(normalizedColor ? { color: normalizedColor } : {}),
          ...(selectedCollection ? { theme: selectedCollection } : {}),
          arrivalDateFrom: selectedArrivalDateFrom,
          arrivalDateTo: selectedArrivalDateTo,
        };
        return searchPurchaseOrders(filters);
      })
    );

    const results = warehouseSearchResults.flat();
    setSearchResults(results);
    setPoSearchResultsContext({
      mode: "vendor-search",
      primaryPoNumber: undefined,
      relatedPurchaseOrders: [],
      helperMessage: undefined,
      helperMessageTone: undefined,
      ...(vendorId.value === undefined ? {} : { vendorName: vendorId.value }),
    });
  } catch (error) {
    setPoSearchResultsContext({
      mode: "flat",
      primaryPoNumber: undefined,
      relatedPurchaseOrders: [],
      helperMessage: `Unable to load purchase orders right now. ${getErrorMessage(error)}`,
      helperMessageTone: "error",
    });
  } finally {
    searching.value = false;
    clearTypedSearchPresentation();
  }
}
const filteredCollectionOptions = computed(() => {
  if (!vendorId.value) {
    return [];
  }

  return collectionOptions.value
    .filter((option) => option.metadata?.vendorId === vendorId.value)
    .toSorted((a, b) => a.label.localeCompare(b.label));
});

// DC determined by the first selected warehouse; constrains remaining picks to that DC.
const selectedDC = computed<string | undefined>(() => {
  const firstId = warehouseIds.value[0];
  if (!firstId) return;
  const [dc] = firstId.split("_");
  return dc;
});

const constrainedWarehouseOptions = computed<DropdownOption[]>(() => {
  if (!selectedDC.value) return warehouseOptions.value;
  return warehouseOptions.value.map((opt) => {
    const division = opt.metadata?.division as string | undefined;
    return division && division !== selectedDC.value
      ? { ...opt, disabled: true }
      : opt;
  });
});

function handleWarehouseDCHeaderClick(dc: string): void {
  const dcWarehouses = warehouseOptions.value
    .filter((opt) => (opt.metadata?.division as string | undefined) === dc)
    .map((opt) => opt.value);
  warehouseIds.value = dcWarehouses;
}

const collectionsDropdownLoading = computed(() => {
  return (
    collectionsLoading.value && filteredCollectionOptions.value.length === 0
  );
});

const collectionsDropdownDisabled = computed(() => {
  return !vendorId.value;
});

let collectionsRequestSequence = 0;

function pruneInvalidCollectionSelections(): void {
  if (collectionsLoading.value || !vendorId.value) {
    return;
  }

  const validCollectionValues = new Set(
    filteredCollectionOptions.value.map((option) => option.value)
  );
  if (
    collection.value !== undefined &&
    !validCollectionValues.has(collection.value)
  ) {
    collection.value = undefined;
  }
}

async function loadCollections(
  selectedVendorId: string | undefined
): Promise<void> {
  const requestedVendorId = selectedVendorId?.trim();
  collectionsRequestSequence += 1;
  const requestSequence = collectionsRequestSequence;
  collectionsError.value = undefined;

  if (!requestedVendorId) {
    collectionOptions.value = [];
    collectionsLoading.value = false;
    pruneInvalidCollectionSelections();
    return;
  }

  collectionOptions.value = getCachedCollections(requestedVendorId);
  collectionsLoading.value = true;

  try {
    const nextOptions = await fetchCollections(requestedVendorId);
    if (
      requestSequence !== collectionsRequestSequence ||
      vendorId.value !== requestedVendorId
    ) {
      return;
    }
    collectionOptions.value = nextOptions;
  } catch {
    if (
      requestSequence !== collectionsRequestSequence ||
      vendorId.value !== requestedVendorId
    ) {
      return;
    }
    if (filteredCollectionOptions.value.length === 0) {
      collectionsError.value = "Failed to load collections.";
    }
  } finally {
    if (
      requestSequence === collectionsRequestSequence &&
      vendorId.value === requestedVendorId
    ) {
      collectionsLoading.value = false;
      pruneInvalidCollectionSelections();
    }
  }
}

const getGroupHeaderLabel = (group: string): string => {
  switch (group) {
    case "FL": {
      return "Florida";
    }
    case "TX": {
      return "Texas";
    }
    case "SE": {
      return "Southeast";
    }
    case "PR": {
      return "Puerto Rico";
    }
    default: {
      return group;
    }
  }
};

watch(vendorId, (nextVendorId, previousVendorId) => {
  if (nextVendorId === previousVendorId) {
    return;
  }

  void loadCollections(nextVendorId);
});

watch([vendorId, filteredCollectionOptions], () => {
  pruneInvalidCollectionSelections();
});

watch(arrivalDateFrom, (newFrom) => {
  if (
    hasSelectedDate(newFrom) &&
    hasSelectedDate(arrivalDateTo.value) &&
    moment(newFrom).isAfter(arrivalDateTo.value, "day")
  ) {
    arrivalDateFrom.value = new Date(arrivalDateTo.value);
  }
});

watch(arrivalDateTo, (newTo) => {
  if (
    hasSelectedDate(newTo) &&
    hasSelectedDate(arrivalDateFrom.value) &&
    moment(newTo).isBefore(arrivalDateFrom.value, "day")
  ) {
    arrivalDateTo.value = new Date(arrivalDateFrom.value);
  }
});

onMounted(async () => {
  if (searchPoNumbersInput.value) {
    poNumbersInput.value = searchPoNumbersInput.value;
  }

  if (vendorId.value) {
    void loadCollections(vendorId.value);
  } else {
    collectionsLoading.value = false;
  }

  const [warehouses, vendors] = await Promise.allSettled([
    fetchWarehouses(),
    fetchVendors(),
  ]);

  if (warehouses.status === "fulfilled") {
    warehouseOptions.value = warehouses.value.filter(
      (x) => x.value !== "PR_99"
    );
  } else {
    warehousesError.value = "Failed to load warehouses.";
  }
  warehousesLoading.value = false;
  if (vendors.status === "fulfilled") {
    vendorOptions.value = vendors.value;
  } else {
    vendorsError.value = "Failed to load vendors.";
  }
  vendorsLoading.value = false;

  rateOfSaleOptions.value = [
    { label: "30 Days", value: "30" },
    { label: "60 Days", value: "60" },
    { label: "90 Days", value: "90" },
  ];
});
</script>

<template>
  <!-- flex-1/min-h-0 lets the card fill .modal-window's fixed height; without it
       short tabs (Bulk Order Lookup) leave the modal's own surface exposed below. -->
  <div
    class="panel-card search-panel-card flex min-h-0 flex-1 flex-col overflow-hidden"
  >
    <div
      v-if="props.showModalChrome"
      class="search-panel-modal-header panel-card-header draggable"
    >
      <span class="search-panel-modal-title">
        Vendor Search / Bulk PO Search
      </span>
      <button
        class="search-panel-modal-close"
        type="button"
        aria-label="Close Vendor Search / Bulk PO Search modal"
        @click="modalStore.close"
      >
        &times;
      </button>
    </div>
    <div class="search-panel-body px-4 py-3 md:px-5 md:py-4">
      <template v-if="props.showTabs">
        <TabGroup v-model="activeSearchTab">
          <template v-if="false">
            <Tab name="purchase-order-search" label="Purchase Order Search">
              <div class="search-panel-layout">
                <div class="min-w-0 flex-1">
                  <FormField label="Find Related POs">
                    <Textbox
                      v-model="relatedPoNumbersInput"
                      placeholder="Enter PO#(s), Cutting Date, Request ID, or Cutting ID"
                      label="Enter PO#(s), Cutting Date, Request ID, or Cutting ID"
                      :value-transformer="normalizeTextboxPoNumbersInput"
                    />
                  </FormField>
                </div>
                <div class="search-panel-action-column">
                  <button
                    class="search-panel-button related-search-button"
                    :disabled="!canSearchRelatedPosFromCuttingDate"
                    :data-tooltip="
                      searchRelatedPosFromCuttingDateDisabledReason
                    "
                    @click="searchRelatedPosFromCuttingDate"
                  >
                    {{ searching ? "⌛ Searching…" : "🔍 Find Related POs" }}
                  </button>
                </div>
              </div>
            </Tab>
          </template>
          <Tab name="Vendor Search" label="Vendor Search">
            <div class="search-panel-layout">
              <!-- Column 1: all fields -->
              <div class="min-w-0 flex-1">
                <!-- Search parameters: dropdowns -->
                <div class="search-panel-fields-row">
                  <FormField
                    label="Vendor"
                    :required="true"
                    class="search-panel-vendor-field"
                  >
                    <Dropdown
                      id="vendor-dropdown"
                      v-model="vendorId"
                      :options="vendorOptions"
                      :loading="vendorsLoading"
                      :filterable="true"
                      placeholder="Select a Vendor"
                      v-bind="vendorsError ? { error: vendorsError } : {}"
                      :auto-select-when="
                        (filterText) => filterText.length === 4
                      "
                    />
                  </FormField>
                  <FormField label="Warehouse" :required="true">
                    <Dropdown
                      v-model:multi-model="warehouseIds"
                      :options="constrainedWarehouseOptions"
                      :loading="warehousesLoading"
                      :multiselect="true"
                      placeholder="Select Warehouses"
                      v-bind="warehousesError ? { error: warehousesError } : {}"
                      :get-group-by="
                        (option: DropdownOption) => {
                          return option.metadata?.division as string;
                        }
                      "
                      :show-group-headers="true"
                      :get-group-header-label="getGroupHeaderLabel"
                      :group-sort-comparator="() => 0"
                      :option-sort-comparator="() => 0"
                      :on-group-header-click="handleWarehouseDCHeaderClick"
                    />
                  </FormField>
                  <FormField label="COLOR">
                    <Textbox
                      v-model="color"
                      placeholder="Enter Color"
                      label="COLOR"
                      :value-transformer="normalizeColor"
                      :value-transformer-delay="COLOR_UPPERCASE_DELAY_MS"
                    />
                  </FormField>
                  <FormField label="Collection">
                    <Dropdown
                      id="collections-dropdown"
                      v-model="collection"
                      :options="filteredCollectionOptions"
                      :loading="collectionsDropdownLoading"
                      :disabled="collectionsDropdownDisabled"
                      :filterable="true"
                      placeholder="Select a Collection"
                      empty-state-text="No matching collections found."
                      loading-state-text="Loading. Please wait."
                      v-bind="
                        collectionsError ? { error: collectionsError } : {}
                      "
                    />
                  </FormField>
                </div>

                <!-- Search parameters: date pickers always on their own row -->
                <div class="search-panel-dates-row">
                  <FormField label="Arrival Date From" :required="true">
                    <DatePicker
                      v-model="arrivalDateFrom"
                      :disabled-date="disabledDateFrom"
                      :parse-transform="clampArrivalDateFrom"
                    />
                  </FormField>
                  <FormField label="Arrival Date To" :required="true">
                    <DatePicker
                      v-model="arrivalDateTo"
                      :disabled-date="disabledDateTo"
                      :parse-transform="clampShipDateTo"
                    />
                  </FormField>
                </div>

                <p
                  v-if="vendorWarehouseHelperParts.length > 0"
                  class="mt-3 text-[13px] leading-5 break-words whitespace-normal text-slate-700"
                >
                  <template
                    v-for="(part, index) in vendorWarehouseHelperParts"
                    :key="`${index.toString()}-${part.text}`"
                  >
                    <strong v-if="part.bold">{{ part.text }}</strong>
                    <span v-else>{{ part.text }}</span>
                  </template>
                </p>
              </div>

              <!-- Column 2: Search button -->
              <div class="search-panel-action-column">
                <button
                  class="search-panel-button"
                  :disabled="!canSearch || searching"
                  :data-tooltip="searchDisabledReason"
                  @click="search"
                >
                  {{ searching ? "⌛ Searching…" : "🔍 Search" }}
                </button>
              </div>
            </div>
          </Tab>

          <Tab name="by-po-numbers" label="PO# Search">
            <div class="search-panel-layout">
              <div class="min-w-0 flex-1">
                <FormField label="PO Numbers">
                  <TextArea
                    v-model="poNumbersInput"
                    placeholder="Enter PO numbers, separated by spaces or new lines"
                    label="PO Numbers"
                    @paste.prevent="onPoNumbersPaste"
                    @blur="onPoNumbersBlur"
                  />
                  <p
                    v-if="poNumbersCount > MAX_PO_NUMBERS"
                    class="mt-1 text-sm text-[#c0392b]"
                    role="alert"
                  >
                    Too many PO numbers ({{ poNumbersCount.toLocaleString() }}).
                    The maximum is {{ MAX_PO_NUMBERS.toLocaleString() }}.
                  </p>
                </FormField>
              </div>
              <div class="search-panel-action-column">
                <button
                  class="search-panel-button"
                  :disabled="!canSearchByPONumbers"
                  :data-tooltip="searchByPONumbersDisabledReason"
                  @click="searchByPONumbers"
                >
                  {{ searching ? "⌛ Searching…" : "🔍 Search" }}
                </button>
              </div>
            </div>
          </Tab>

          <Tab name="bulk-order-lookup" label="Bulk Order Lookup">
            <div class="search-panel-layout">
              <div class="min-w-0 flex-1">
                <FormField label="Bulk Order / Intermediate PO Number(s)">
                  <Textbox
                    v-model="bulkOrderNumber"
                    placeholder="e.g. Z1722591JGO or IPO1722598KLM"
                    label="Bulk Order / Intermediate PO Number(s)"
                    @keydown.enter="searchBulkOrderIntermediateOrders"
                  />
                </FormField>
              </div>
              <div class="search-panel-action-column">
                <button
                  class="search-panel-button"
                  :disabled="searching || !bulkOrderNumber.trim()"
                  @click="searchBulkOrderIntermediateOrders"
                >
                  {{ searching ? "⌛ Searching…" : "🔍 Search" }}
                </button>
              </div>
            </div>
          </Tab>
        </TabGroup>
      </template>
    </div>
  </div>
</template>
